/**
 * Same-origin client for the signed Fur & Far Shopify App Proxy.
 * Customer identity is added and authenticated by Shopify, never by this script.
 */
(() => {
  if (window.FurFarApi) return;

  const ACTIONS = new Set([
    'state',
    'save_pet',
    'delete_pet',
    'toggle_wishlist',
    'import_legacy',
    'photo_upload_url',
    'photo_finalize',
    'photo_url',
  ]);
  const MUTATIONS = new Set([
    'save_pet',
    'delete_pet',
    'toggle_wishlist',
    'import_legacy',
    'photo_upload_url',
    'photo_finalize',
  ]);
  const PUBLIC_MESSAGES = {
    authentication_required: 'Please sign in to continue.',
    invalid_payload: 'We could not process that request. Check the details and try again.',
    invalid_idempotency_key: 'We could not safely save that change. Please try again.',
    version_conflict: 'This record changed on another device. Review the saved version and try again.',
    idempotency_conflict: 'That change conflicts with an earlier request. Refresh and try again.',
    not_found: 'That record could not be found.',
    rate_limited: 'Too many requests were made. Please wait a moment and try again.',
    service_unavailable: 'Fur & Far account data is temporarily unavailable. Please try again.',
  };

  class FurFarApiError extends Error {
    constructor(code, status, correlationId = '') {
      super(PUBLIC_MESSAGES[code] || PUBLIC_MESSAGES.service_unavailable);
      this.name = 'FurFarApiError';
      this.code = code;
      this.status = status;
      this.correlationId = correlationId;
      this.isConflict = status === 409;
    }
  }

  const containsCustomerIdentity = (value) => {
    if (Array.isArray(value)) return value.some(containsCustomerIdentity);
    if (!value || typeof value !== 'object') return false;
    return Object.entries(value).some(([key, child]) => (
      key === 'customer_id' || key === 'shopify_customer_id' || containsCustomerIdentity(child)
    ));
  };

  const loginHref = () => {
    const base = document.body.dataset.loginUrl || '/customer_authentication/login';
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    try {
      const url = new URL(base, window.location.origin);
      url.searchParams.set('return_to', returnTo);
      return `${url.pathname}${url.search}${url.hash}`;
    } catch (error) {
      const separator = base.includes('?') ? '&' : '?';
      return `${base}${separator}return_to=${encodeURIComponent(returnTo)}`;
    }
  };

  const safeError = async (response) => {
    let body = {};
    try {
      body = await response.json();
    } catch (error) {
      body = {};
    }
    const serverError = body && body.error && typeof body.error === 'object' ? body.error : {};
    const serverCode = typeof serverError.code === 'string' ? serverError.code : '';
    const correlationId = typeof serverError.correlationId === 'string' ? serverError.correlationId : '';
    if (response.status === 401) return new FurFarApiError('authentication_required', 401, correlationId);
    if (response.status === 409) return new FurFarApiError(serverCode || 'version_conflict', 409, correlationId);
    if (response.status === 429) return new FurFarApiError('rate_limited', 429, correlationId);
    if (response.status >= 500) return new FurFarApiError('service_unavailable', response.status, correlationId);
    return new FurFarApiError(PUBLIC_MESSAGES[serverCode] ? serverCode : 'invalid_payload', response.status, correlationId);
  };

  const createClient = (dependencies = {}) => {
    const fetchImpl = dependencies.fetchImpl || window.fetch.bind(window);
    const random = dependencies.random || Math.random;
    const sleep = dependencies.sleep || ((milliseconds) => new Promise((resolve) => setTimeout(resolve, milliseconds)));
    const uuid = dependencies.uuid || (() => crypto.randomUUID());
    const baseUrl = dependencies.baseUrl || document.body.dataset.storefrontDataUrl || '/apps/fur-far-data';

    const request = async (action, options = {}) => {
      if (!ACTIONS.has(action)) throw new FurFarApiError('invalid_payload', 422);
      const method = options.method || (MUTATIONS.has(action) ? 'POST' : 'GET');
      const body = options.body;
      if (containsCustomerIdentity(body)) throw new FurFarApiError('invalid_payload', 422);

      const url = new URL(baseUrl, window.location.origin);
      url.searchParams.set('action', action);
      Object.entries(options.query || {}).forEach(([key, value]) => {
        if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
      });
      const target = `${url.pathname}${url.search}`;
      const headers = { accept: 'application/json' };
      const idempotencyKey = MUTATIONS.has(action)
        ? String(options.idempotencyKey || uuid())
        : '';
      if (MUTATIONS.has(action)) {
        headers['content-type'] = 'application/json';
        headers['idempotency-key'] = idempotencyKey;
      }

      for (let attempt = 0; attempt < 3; attempt += 1) {
        let response;
        try {
          response = await fetchImpl(target, {
            method,
            credentials: 'same-origin',
            headers,
            body: MUTATIONS.has(action) ? JSON.stringify(body || {}) : undefined,
          });
        } catch (error) {
          if (attempt === 2) throw new FurFarApiError('service_unavailable', 0);
          await sleep((250 * (2 ** attempt)) + Math.floor(random() * 100));
          continue;
        }

        if (response.ok) {
          let payload;
          try {
            payload = await response.json();
          } catch (error) {
            throw new FurFarApiError('service_unavailable', response.status);
          }
          if (!payload || !Object.prototype.hasOwnProperty.call(payload, 'data')) {
            throw new FurFarApiError('service_unavailable', response.status);
          }
          if (payload.data && Array.isArray(payload.data.pets) && Array.isArray(payload.data.wishlists)) {
            document.dispatchEvent(new CustomEvent('ff:storefront-state', { detail: payload.data }));
          }
          return { ok: true, data: payload.data };
        }

        if ((response.status === 429 || response.status >= 500) && attempt < 2) {
          await sleep((250 * (2 ** attempt)) + Math.floor(random() * 100));
          continue;
        }

        const error = await safeError(response);
        if (response.status === 401) window.location.href = loginHref();
        throw error;
      }

      throw new FurFarApiError('service_unavailable', 0);
    };

    return {
      request,
      state: () => request('state', { method: 'GET' }),
      savePet: (pet, expectedVersion) => request('save_pet', {
        body: { pet, expected_version: expectedVersion == null ? null : String(expectedVersion) },
      }),
      deletePet: (petId, expectedVersion) => request('delete_pet', {
        body: { pet_id: String(petId), expected_version: String(expectedVersion) },
      }),
      toggleWishlist: (wishlist) => request('toggle_wishlist', { body: wishlist }),
      importLegacy: (payload, importHash) => request('import_legacy', {
        body: payload,
        idempotencyKey: importHash,
      }),
      createPhotoUpload: (petId, extension) => request('photo_upload_url', {
        body: { pet_id: String(petId), extension },
      }),
      finalizePhoto: (petId, photoId, path) => request('photo_finalize', {
        body: { pet_id: String(petId), photo_id: String(photoId), path },
      }),
      uploadPhoto: async (upload, blob) => {
        const signedUrl = upload && typeof upload.signedUrl === 'string' ? upload.signedUrl : '';
        let url;
        try {
          url = new URL(signedUrl);
        } catch (error) {
          throw new FurFarApiError('invalid_payload', 422);
        }
        if (
          url.protocol !== 'https:'
          || !(blob instanceof Blob)
          || blob.size < 1
          || blob.size > 2_097_152
          || !['image/jpeg', 'image/webp'].includes(blob.type)
        ) {
          throw new FurFarApiError('invalid_payload', 422);
        }
        const form = new FormData();
        form.append('cacheControl', '3600');
        form.append('', blob);
        let response;
        try {
          response = await fetchImpl(url.href, {
            method: 'PUT',
            credentials: 'omit',
            body: form,
          });
        } catch (error) {
          throw new FurFarApiError('service_unavailable', 0);
        }
        if (!response.ok) throw await safeError(response);
        return { ok: true };
      },
      photoUrl: (photoId) => request('photo_url', {
        method: 'GET',
        query: { photo_id: String(photoId) },
      }),
    };
  };

  window.FurFarApi = Object.assign(createClient(), {
    createClient,
    FurFarApiError,
  });
})();
